import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useContent } from "@/lib/content";
import { api, formatApiError } from "@/lib/api";
import ClubsPanel from "@/pages/admin/ClubsPanel";
import AdminUrlsPanel from "@/pages/admin/AdminUrlsPanel";
import VenuesPanel from "@/pages/admin/VenuesPanel";
import MembersPanel from "@/pages/admin/MembersPanel";
import EnjoyOverlaysPanel from "@/pages/admin/EnjoyOverlaysPanel";
import SignatureQueuePanel from "@/pages/admin/SignatureQueuePanel";
import EscapeCataloguePanel from "@/pages/admin/EscapeCataloguePanel";
import BiblePanel from "@/pages/admin/BiblePanel";

const EDITABLE_KEYS = [
    { key: "hero_headline", label: "Hero Headline (serif, large)", textarea: true },
    { key: "hero_subheadline", label: "Hero Sub-headline", textarea: true },
    { key: "mira_intro_above", label: "Mira section · Headline above embed" },
    { key: "mira_intro_below", label: "Mira section · Sub-line above embed", textarea: true },
    { key: "mira_handoff_note", label: "Mira section · Handoff note (italic)", textarea: true },
    { key: "credentials_line", label: "Credentials line (single sentence)", textarea: true },
    { key: "invisible_workload_headline", label: "Invisible Workload · Headline" },
    { key: "invisible_workload_body", label: "Invisible Workload · Body (two paragraphs separated by blank line)", textarea: true, big: true },
    { key: "pull_quote", label: "Pull-quote · Body", textarea: true },
    { key: "pull_quote_attribution", label: "Pull-quote · Attribution" },
    { key: "manifesto", label: "The Manifesto (most important paragraph)", textarea: true, big: true },
    { key: "recognition_line", label: "Recognition line", textarea: true },
    { key: "form_invitation", label: "Pilot form · Invitation", textarea: true },
    { key: "whatsapp_alt", label: "WhatsApp alt (form fallback)" },
    { key: "whatsapp_number", label: "WhatsApp number (digits only, with country code)" },
    { key: "contact_email", label: "Contact email" },
];

export function Editor({ initial, onSaved }) {
    const [vals, setVals] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        setVals(initial);
    }, [initial]);

    const set = (k) => (e) => setVals((v) => ({ ...v, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        setMsg("");
        setErr("");
        try {
            const blocks = EDITABLE_KEYS.map((f) => ({ key: f.key, value: vals[f.key] ?? "" }));
            await api.put("/content", { blocks });
            setMsg("Saved.");
            onSaved && onSaved();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-8">
            {EDITABLE_KEYS.map((f) => (
                <div key={f.key} className="field" data-testid={`content-field-${f.key}`}>
                    <label>{f.label}</label>
                    {f.textarea ? (
                        <textarea
                            rows={f.big ? 8 : 3}
                            value={vals[f.key] ?? ""}
                            onChange={set(f.key)}
                            data-testid={`content-input-${f.key}`}
                        />
                    ) : (
                        <input value={vals[f.key] ?? ""} onChange={set(f.key)} data-testid={`content-input-${f.key}`} />
                    )}
                </div>
            ))}
            <div className="flex items-center gap-6 pt-2">
                <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="content-save-button">
                    {saving ? "Saving…" : "Save changes"}
                </button>
                {msg && <span className="eyebrow text-[hsl(var(--brass))]" data-testid="content-save-message">{msg}</span>}
                {err && <span className="text-[13px] text-[hsl(var(--destructive))]">{err}</span>}
            </div>
        </div>
    );
}

export function Enquiries() {
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/enquiries");
                setItems(data);
            } catch (e) {
                setErr(formatApiError(e.response?.data?.detail) || e.message);
            }
        })();
    }, []);

    if (err) return <p className="text-[13px] text-[hsl(var(--destructive))]" data-testid="enquiries-error">{err}</p>;
    if (!items) return <p className="eyebrow" data-testid="enquiries-loading">Loading…</p>;
    if (items.length === 0)
        return <p className="text-[15px] text-[hsl(var(--muted))]" data-testid="enquiries-empty">No pilot enquiries yet.</p>;

    return (
        <div className="overflow-x-auto" data-testid="enquiries-table">
            <table className="w-full text-[14px]">
                <thead>
                    <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] border-b border-[hsl(var(--brass)/0.4)]">
                        <th className="py-3 pr-4">Received</th>
                        <th className="py-3 pr-4">Name</th>
                        <th className="py-3 pr-4">Role</th>
                        <th className="py-3 pr-4">Company</th>
                        <th className="py-3 pr-4">City</th>
                        <th className="py-3 pr-4">Email</th>
                        <th className="py-3 pr-4">Phone</th>
                        <th className="py-3 pr-4">Note</th>
                        <th className="py-3 pr-4">Email sent?</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((e) => (
                        <tr key={e.id} className="border-b border-[hsl(var(--brass)/0.2)]" data-testid={`enquiry-row-${e.id}`}>
                            <td className="py-3 pr-4 text-[hsl(var(--muted))]">{new Date(e.created_at).toLocaleString()}</td>
                            <td className="py-3 pr-4 font-medium">{e.name}</td>
                            <td className="py-3 pr-4">{e.role}</td>
                            <td className="py-3 pr-4">{e.company}</td>
                            <td className="py-3 pr-4">{e.city}</td>
                            <td className="py-3 pr-4"><a href={`mailto:${e.email}`} className="hover:text-[hsl(var(--brass))]">{e.email}</a></td>
                            <td className="py-3 pr-4">{e.phone}</td>
                            <td className="py-3 pr-4 max-w-xs">{e.note || "—"}</td>
                            <td className="py-3 pr-4">{e.email_sent ? "Yes" : "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const STATUS_LABELS = {
    new: "New",
    in_progress: "In progress",
    closed: "Closed",
};

function StatusPill({ status }) {
    const map = {
        new: "bg-[hsl(var(--brass)/0.18)] text-[hsl(var(--brass))] border-[hsl(var(--brass))]",
        in_progress: "bg-[hsl(var(--navy)/0.08)] text-[hsl(var(--navy))] border-[hsl(var(--navy)/0.4)]",
        closed: "bg-transparent text-[hsl(var(--muted))] border-[hsl(var(--muted)/0.4)]",
    };
    return (
        <span
            className={`inline-block px-3 py-1 text-[10px] tracking-[0.18em] uppercase border ${map[status] || map.new}`}
            data-testid={`status-pill-${status}`}
        >
            {STATUS_LABELS[status] || status}
        </span>
    );
}

function ChannelBadge({ channel }) {
    const map = {
        web: { label: "Web", cls: "border-[hsl(var(--brass)/0.5)] text-[hsl(var(--muted))]" },
        whatsapp: { label: "WhatsApp", cls: "border-[#25D366] text-[#1B9E50]" },
        email: { label: "Email", cls: "border-[hsl(var(--navy)/0.5)] text-[hsl(var(--navy))]" },
        "in-app": { label: "In-app", cls: "border-[hsl(var(--brass)/0.5)] text-[hsl(var(--brass))]" },
    };
    const { label, cls } = map[channel] || map.web;
    return (
        <span
            className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border ${cls}`}
            data-testid={`channel-badge-${channel || "web"}`}
        >
            {label}
        </span>
    );
}

function WindowBadge({ expiresAt, channel }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);
    if (channel !== "whatsapp" || !expiresAt) return null;
    const remainingMs = new Date(expiresAt).getTime() - now;
    if (remainingMs <= 0) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] uppercase border border-[hsl(var(--destructive)/0.5)] text-[hsl(var(--destructive))]"
                data-testid="window-badge-closed"
            >
                ● Window closed · template required
            </span>
        );
    }
    const hrs = Math.floor(remainingMs / (60 * 60 * 1000));
    const colour = hrs >= 3
        ? "border-[#1B9E50] text-[#1B9E50]"
        : "border-[#c98a00] text-[#c98a00]";
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] uppercase border ${colour}`}
            data-testid="window-badge-open"
        >
            ● Window open · {hrs}h remaining
        </span>
    );
}

function WhatsAppReplyBox({ ticket, onChange }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState("");
    const [mode, setMode] = useState("text"); // "text" | "template"
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState("");
    const [vars, setVars] = useState({});
    const [showTemplates, setShowTemplates] = useState(false);
    const [diag, setDiag] = useState(null);
    const [resuming, setResuming] = useState(false);

    // Compute window state
    const windowExp = ticket.channel_window_expires_at;
    const windowClosed = windowExp ? new Date(windowExp).getTime() < Date.now() : true;

    useEffect(() => {
        if (windowClosed && mode === "text") setMode("template");
    }, [windowClosed, mode]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [{ data: tmpl }, { data: d }] = await Promise.all([
                    api.get("/admin/whatsapp/templates"),
                    api.get("/admin/whatsapp/diagnostics"),
                ]);
                if (cancelled) return;
                setTemplates(tmpl || []);
                setDiag(d || null);
                if (tmpl && tmpl.length > 0 && !templateName) {
                    setTemplateName(tmpl[0].name);
                }
            } catch {
                /* silent */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ticket.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentTemplate = templates.find((t) => t.name === templateName);
    const placeholders = currentTemplate
        ? Array.from((currentTemplate.body || "").matchAll(/\{\{(\d+)\}\}/g)).map((m) => m[1])
        : [];

    const sendText = async () => {
        setSending(true);
        setMsg("");
        try {
            const { data } = await api.post(`/admin/mira/tickets/${ticket.id}/reply-whatsapp`, { text });
            setMsg("Sent.");
            setText("");
            // refetch the ticket so transcript updates
            const fresh = await api.get(`/admin/mira/tickets/${ticket.id}`);
            onChange && onChange(fresh.data);
        } catch (e) {
            setMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSending(false);
        }
    };

    const sendTemplate = async () => {
        setSending(true);
        setMsg("");
        try {
            const variables = placeholders.map((p) => vars[p] || "");
            await api.post(`/admin/mira/tickets/${ticket.id}/send-template`, {
                template_name: templateName,
                variables,
            });
            setMsg(`Template '${templateName}' sent.`);
            setVars({});
            const fresh = await api.get(`/admin/mira/tickets/${ticket.id}`);
            onChange && onChange(fresh.data);
        } catch (e) {
            setMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSending(false);
        }
    };

    const resumeMira = async () => {
        setResuming(true);
        try {
            await api.post(`/admin/mira/tickets/${ticket.id}/resume-mira`);
            const fresh = await api.get(`/admin/mira/tickets/${ticket.id}`);
            onChange && onChange(fresh.data);
        } catch (e) {
            setMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setResuming(false);
        }
    };

    const notConfigured = diag && !diag.configured;

    return (
        <div className="mb-8 p-5 border border-[hsl(var(--brass)/0.5)] bg-[hsl(var(--paper))]" data-testid="whatsapp-reply-box">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                <p className="eyebrow">Reply on WhatsApp</p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setMode("text")}
                        disabled={windowClosed}
                        className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1 border ${mode === "text" ? "border-[hsl(var(--brass))] text-[hsl(var(--brass))]" : "border-transparent text-[hsl(var(--muted))]"} disabled:opacity-40`}
                        data-testid="reply-mode-text"
                    >
                        Free text
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("template")}
                        className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1 border ${mode === "template" ? "border-[hsl(var(--brass))] text-[hsl(var(--brass))]" : "border-transparent text-[hsl(var(--muted))]"}`}
                        data-testid="reply-mode-template"
                    >
                        Template
                    </button>
                </div>
            </div>

            {notConfigured && (
                <p className="text-[12px] text-[hsl(var(--destructive))] mb-3" data-testid="whatsapp-not-configured">
                    Gupshup credentials not yet active — sending is disabled. Plumbing is ready; paste the API key when available.
                </p>
            )}

            {mode === "text" ? (
                <div>
                    <textarea
                        rows={3}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={windowClosed ? "Window closed — switch to template above." : "Type a reply to send on WhatsApp…"}
                        disabled={windowClosed}
                        className="w-full p-3 text-[14px] bg-[hsl(var(--ivory))] border border-[hsl(var(--brass)/0.4)] focus:outline-none focus:border-[hsl(var(--brass))] disabled:opacity-50"
                        data-testid="whatsapp-reply-text"
                    />
                    <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <button
                            type="button"
                            onClick={sendText}
                            disabled={sending || !text.trim() || windowClosed || notConfigured}
                            className="btn-primary disabled:opacity-50"
                            data-testid="whatsapp-send-text"
                        >
                            {sending ? "Sending…" : "↗ Send on WhatsApp"}
                        </button>
                        {ticket.mira_paused && (
                            <button
                                type="button"
                                onClick={resumeMira}
                                disabled={resuming}
                                className="btn-ghost text-[11px] tracking-[0.2em] uppercase disabled:opacity-60"
                                data-testid="whatsapp-resume-mira"
                            >
                                {resuming ? "Resuming…" : "↶ Hand back to Mira"}
                            </button>
                        )}
                        {msg && <span className="eyebrow text-[hsl(var(--brass))]" data-testid="whatsapp-reply-msg">{msg}</span>}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="field">
                            <label>Template</label>
                            <select
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                className="w-full p-3 text-[14px] bg-[hsl(var(--ivory))] border border-[hsl(var(--brass)/0.4)] focus:outline-none focus:border-[hsl(var(--brass))]"
                                data-testid="whatsapp-template-select"
                            >
                                {templates.map((t) => (
                                    <option key={t.name} value={t.name}>
                                        {t.name} {t.status !== "approved" ? `(${t.status})` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {currentTemplate && (
                        <p className="mt-3 text-[13px] italic text-[hsl(var(--muted))]" data-testid="whatsapp-template-preview">
                            {currentTemplate.body}
                        </p>
                    )}
                    {placeholders.length > 0 && (
                        <div className="mt-3 grid md:grid-cols-3 gap-3">
                            {placeholders.map((p) => (
                                <div className="field" key={p}>
                                    <label>{`{{${p}}}`}</label>
                                    <input
                                        type="text"
                                        value={vars[p] || ""}
                                        onChange={(e) => setVars({ ...vars, [p]: e.target.value })}
                                        className="w-full p-2 text-[14px] bg-[hsl(var(--ivory))] border border-[hsl(var(--brass)/0.4)] focus:outline-none focus:border-[hsl(var(--brass))]"
                                        data-testid={`whatsapp-template-var-${p}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <button
                            type="button"
                            onClick={sendTemplate}
                            disabled={sending || !templateName || notConfigured}
                            className="btn-primary disabled:opacity-50"
                            data-testid="whatsapp-send-template"
                        >
                            {sending ? "Sending…" : "↗ Send template"}
                        </button>
                        {msg && <span className="eyebrow text-[hsl(var(--brass))]">{msg}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}

function MiraTicketDetail({ ticket, onChange, onClose }) {
    const [status, setStatus] = useState(ticket.status);
    const [notes, setNotes] = useState(ticket.notes || "");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    // Forward state
    const [showForward, setShowForward] = useState(false);
    const [forwardTo, setForwardTo] = useState("");
    const [forwardCc, setForwardCc] = useState("");
    const [forwardNote, setForwardNote] = useState("");
    const [forwarding, setForwarding] = useState(false);
    const [forwardMsg, setForwardMsg] = useState("");

    const save = async () => {
        setSaving(true);
        setMsg("");
        try {
            const { data } = await api.patch(`/admin/mira/tickets/${ticket.id}`, { status, notes });
            setMsg("Saved.");
            onChange && onChange(data);
        } catch (e) {
            setMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const sendForward = async () => {
        setForwarding(true);
        setForwardMsg("");
        try {
            const cc = forwardCc
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            await api.post(`/admin/mira/tickets/${ticket.id}/forward`, {
                to: forwardTo.trim(),
                cc,
                note: forwardNote.trim() || null,
            });
            setForwardMsg(`Forwarded to ${forwardTo.trim()}.`);
            setForwardTo("");
            setForwardCc("");
            setForwardNote("");
            setTimeout(() => {
                setShowForward(false);
                setForwardMsg("");
            }, 1800);
        } catch (e) {
            setForwardMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setForwarding(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-[hsl(var(--navy)/0.85)] overflow-y-auto" data-testid="mira-ticket-detail">
            <div className="min-h-full flex items-start justify-center p-4 md:p-12">
                <div className="bg-[hsl(var(--ivory))] w-full max-w-4xl border border-[hsl(var(--brass))] shadow-2xl">
                    <div className="flex items-start justify-between gap-6 p-6 md:p-10 border-b border-[hsl(var(--brass)/0.4)]">
                        <div className="min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                                <p className="eyebrow">{ticket.source}</p>
                                <ChannelBadge channel={ticket.channel || "web"} />
                                <WindowBadge expiresAt={ticket.channel_window_expires_at} channel={ticket.channel} />
                                {ticket.mira_paused && (
                                    <span
                                        className="inline-block px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border border-[hsl(var(--brass))] text-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.08)]"
                                        title={`Human handoff: ${ticket.handoff_taken_by || ""}`}
                                        data-testid="mira-paused-badge"
                                    >
                                        ▌ Mira paused
                                    </span>
                                )}
                            </div>
                            <h2 className="font-serif text-2xl md:text-3xl mt-2">
                                {ticket.lead_name || "Anonymous visitor"}
                            </h2>
                            <p className="mt-2 text-[13px] text-[hsl(var(--muted))]">
                                {new Date(ticket.created_at).toLocaleString()}
                                {ticket.lead_email && (
                                    <>
                                        {" · "}
                                        <a href={`mailto:${ticket.lead_email}`} className="hover:text-[hsl(var(--brass))]">{ticket.lead_email}</a>
                                    </>
                                )}
                                {ticket.lead_phone && (
                                    <>
                                        {" · "}
                                        <a href={`https://wa.me/${ticket.lead_phone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--brass))]">{ticket.lead_phone}</a>
                                    </>
                                )}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--muted))] hover:text-[hsl(var(--brass))]"
                            data-testid="ticket-close-button"
                        >
                            Close ✕
                        </button>
                    </div>

                    <div className="p-6 md:p-10 grid md:grid-cols-12 gap-8">
                        {/* Transcript */}
                        <div className="md:col-span-8">
                            {ticket.channel === "whatsapp" && (
                                <WhatsAppReplyBox ticket={ticket} onChange={onChange} />
                            )}
                            <p className="eyebrow">Transcript</p>
                            <div className="mt-5 space-y-3" data-testid="ticket-transcript">
                                {(ticket.transcript || []).length === 0 && (
                                    <p className="text-[14px] text-[hsl(var(--muted))]">
                                        Transcript not yet available — Chatbase will enrich this conversation shortly.
                                    </p>
                                )}
                                {(ticket.transcript || []).map((m, i) => (
                                    <div
                                        key={i}
                                        className={`p-4 border ${
                                            m.role === "user"
                                                ? "bg-[hsl(var(--cream))] border-[hsl(var(--brass)/0.4)]"
                                                : "bg-[hsl(var(--paper))] border-[hsl(var(--navy)/0.15)]"
                                        }`}
                                        data-testid={`transcript-msg-${i}`}
                                    >
                                        <p className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--muted))]">
                                            {m.role === "user" ? "Visitor" : m.role === "assistant" ? "Mira" : m.role}
                                        </p>
                                        <p className="mt-2 text-[14px] leading-relaxed whitespace-pre-wrap text-[hsl(var(--ink))]">
                                            {m.content}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Side panel */}
                        <div className="md:col-span-4 space-y-8">
                            <div>
                                <p className="eyebrow">Status</p>
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="mt-3 w-full p-3 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.6)] focus:outline-none focus:border-[hsl(var(--brass))]"
                                    data-testid="ticket-status-select"
                                >
                                    <option value="new">New</option>
                                    <option value="in_progress">In progress</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>

                            <div>
                                <p className="eyebrow">Internal notes</p>
                                <textarea
                                    rows={6}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="mt-3 w-full p-3 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.6)] focus:outline-none focus:border-[hsl(var(--brass))]"
                                    placeholder="What did the team do? Who is owning this?"
                                    data-testid="ticket-notes-textarea"
                                />
                            </div>

                            <div>
                                <p className="eyebrow">Meta</p>
                                <dl className="mt-3 text-[12px] text-[hsl(var(--muted))] space-y-1.5">
                                    <div className="flex justify-between gap-3"><dt>Conversation</dt><dd className="font-mono text-[11px] text-[hsl(var(--ink))] truncate max-w-[180px]">{ticket.conversation_id || "—"}</dd></div>
                                    <div className="flex justify-between gap-3"><dt>Email sent</dt><dd className="text-[hsl(var(--ink))]">{ticket.email_sent ? "Yes" : "Pending"}</dd></div>
                                    {ticket.lead_company && (
                                        <div className="flex justify-between gap-3"><dt>Company</dt><dd className="text-[hsl(var(--ink))]">{ticket.lead_company}</dd></div>
                                    )}
                                </dl>
                            </div>

                            <div className="flex items-center gap-4 pt-2 flex-wrap">
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    className="btn-primary disabled:opacity-60"
                                    data-testid="ticket-save-button"
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowForward((v) => !v)}
                                    className="btn-ghost text-[12px] tracking-[0.2em] uppercase"
                                    data-testid="ticket-forward-toggle"
                                >
                                    {showForward ? "Cancel" : "↗ Forward"}
                                </button>
                                {msg && <span className="eyebrow text-[hsl(var(--brass))]" data-testid="ticket-save-message">{msg}</span>}
                            </div>

                            {showForward && (
                                <div
                                    className="mt-4 p-5 bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.5)]"
                                    data-testid="ticket-forward-form"
                                >
                                    <p className="eyebrow">Forward this conversation</p>

                                    <div className="mt-4 field">
                                        <label htmlFor="fwd-to">Send to</label>
                                        <input
                                            id="fwd-to"
                                            type="email"
                                            value={forwardTo}
                                            onChange={(e) => setForwardTo(e.target.value)}
                                            placeholder="harshad@lesconcierges.co.in"
                                            autoComplete="off"
                                            data-testid="ticket-forward-to"
                                        />
                                    </div>

                                    <div className="mt-3 field">
                                        <label htmlFor="fwd-cc">CC (optional, comma-separated)</label>
                                        <input
                                            id="fwd-cc"
                                            type="text"
                                            value={forwardCc}
                                            onChange={(e) => setForwardCc(e.target.value)}
                                            placeholder="srikanth.ds@lesconcierges.co.in"
                                            autoComplete="off"
                                            data-testid="ticket-forward-cc"
                                        />
                                    </div>

                                    <div className="mt-3 field">
                                        <label htmlFor="fwd-note">A short note (optional)</label>
                                        <textarea
                                            id="fwd-note"
                                            rows={3}
                                            value={forwardNote}
                                            onChange={(e) => setForwardNote(e.target.value)}
                                            placeholder="Hi, please pick this up — passport renewal in Whitefield."
                                            data-testid="ticket-forward-note"
                                            maxLength={500}
                                        />
                                    </div>

                                    <div className="mt-4 flex items-center gap-4 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={sendForward}
                                            disabled={forwarding || !forwardTo.trim()}
                                            className="btn-primary disabled:opacity-60"
                                            data-testid="ticket-forward-send"
                                        >
                                            {forwarding ? "Sending…" : "Send forward"}
                                        </button>
                                        {forwardMsg && (
                                            <span
                                                className="eyebrow text-[hsl(var(--brass))]"
                                                data-testid="ticket-forward-message"
                                            >
                                                {forwardMsg}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {ticket.forwards && ticket.forwards.length > 0 && (
                                <div className="mt-6 pt-5 border-t border-[hsl(var(--brass)/0.3)]" data-testid="ticket-forwards-history">
                                    <p className="eyebrow">Previously forwarded</p>
                                    <ul className="mt-3 space-y-1.5 text-[12px] text-[hsl(var(--muted))]">
                                        {ticket.forwards.map((f, i) => (
                                            <li key={i}>
                                                <span className="text-[hsl(var(--ink))]">{f.to}</span>
                                                {f.cc && f.cc.length > 0 && (
                                                    <span> · cc {f.cc.join(", ")}</span>
                                                )}
                                                <span> · by {f.by}</span>
                                                <span> · {new Date(f.at).toLocaleString()}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* v3.32 — Founder audit view. Delivery log
                                + a direct link to the Sourcing Ops Docket
                                so any concierge or the founder can verify
                                what actually went out, with timestamps. */}
                            {(ticket.delivery_log && ticket.delivery_log.length > 0) || ticket.channel === "web_basket" ? (
                                <div className="mt-6 pt-5 border-t border-[hsl(var(--brass)/0.3)]" data-testid="ticket-audit-view">
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                        <p className="eyebrow">Delivery log · audit</p>
                                        <a
                                            href={`/api/lc/ticket/${ticket.id}/ops`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] font-mono tracking-[0.08em] text-[hsl(var(--brass))] hover:text-[hsl(var(--ink))] underline underline-offset-2"
                                            data-testid="ticket-ops-docket-link"
                                        >
                                            View Ops Docket ↗
                                        </a>
                                    </div>
                                    <ul className="mt-3 space-y-2 text-[12px] text-[hsl(var(--muted))]">
                                        {(ticket.delivery_log || []).map((e, i) => {
                                            const t = e.timestamp ? new Date(e.timestamp) : null;
                                            const colorByStatus = {
                                                ok: "hsl(var(--success, 142 50% 35%))",
                                                sent: "hsl(var(--success, 142 50% 35%))",
                                                skipped: "hsl(var(--muted))",
                                                error: "hsl(var(--destructive, 0 65% 50%))",
                                            };
                                            const dotColor = colorByStatus[e.status] || "hsl(var(--muted))";
                                            return (
                                                <li
                                                    key={i}
                                                    data-testid={`audit-event-${i}`}
                                                    data-event={e.event}
                                                    data-status={e.status}
                                                    className="flex items-start gap-3"
                                                >
                                                    <span
                                                        className="inline-block flex-none mt-1.5"
                                                        style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor }}
                                                    />
                                                    <span className="flex-1 min-w-0">
                                                        <span className="font-mono text-[hsl(var(--ink))] text-[11px] tracking-[0.04em]">
                                                            {String(e.event).replace(/_/g, " ")}
                                                        </span>
                                                        {e.recipient && (
                                                            <span className="text-[hsl(var(--muted))]"> · to <span className="text-[hsl(var(--ink))]">{e.recipient}</span></span>
                                                        )}
                                                        {Array.isArray(e.cc) && e.cc.length > 0 && (
                                                            <span className="text-[hsl(var(--muted))]"> · cc <span className="text-[hsl(var(--ink))]">{e.cc.join(", ")}</span></span>
                                                        )}
                                                        {e.subject && (
                                                            <span className="block text-[10px] text-[hsl(var(--muted))] mt-0.5">subject: {e.subject}</span>
                                                        )}
                                                        {e.error && (
                                                            <span className="block text-[10px] text-[hsl(var(--destructive,0_65%_50%))] mt-0.5 font-mono">{e.error}</span>
                                                        )}
                                                        {e.detail && (
                                                            <span className="block text-[10px] text-[hsl(var(--muted))] mt-0.5 font-mono">{e.detail}</span>
                                                        )}
                                                        {e.provider_id && (
                                                            <span className="block text-[10px] text-[hsl(var(--muted))] mt-0.5 font-mono">{e.provider}#{e.provider_id}</span>
                                                        )}
                                                        {t && (
                                                            <span className="block text-[10px] text-[hsl(var(--muted))] mt-0.5">{t.toLocaleString()}</span>
                                                        )}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                        {(!ticket.delivery_log || ticket.delivery_log.length === 0) && (
                                            <li className="text-[hsl(var(--muted))] italic">No delivery events recorded.</li>
                                        )}
                                    </ul>
                                </div>
                            ) : null}

                            {/* v3.32 — Full conversation transcript (always
                                visible when present — the brief travels
                                with the chat itself, no separate lookup). */}
                            {ticket.conversation_transcript && ticket.conversation_transcript.length > 0 && (
                                <details className="mt-6 pt-5 border-t border-[hsl(var(--brass)/0.3)]" data-testid="ticket-transcript">
                                    <summary className="eyebrow cursor-pointer">
                                        Full conversation · {ticket.conversation_transcript.length} message{ticket.conversation_transcript.length !== 1 ? "s" : ""}
                                    </summary>
                                    <div className="mt-3 space-y-3 text-[13px]">
                                        {ticket.conversation_transcript.map((m, i) => (
                                            <div key={i} data-testid={`transcript-msg-${i}`} data-role={m.role}>
                                                <div className="text-[10px] font-mono tracking-[0.08em] uppercase mb-0.5"
                                                     style={{ color: m.role === "user" ? "hsl(var(--ink))" : "hsl(var(--brass))" }}>
                                                    {m.role === "user" ? "Member" : "Mira"}
                                                </div>
                                                <div className="whitespace-pre-wrap text-[hsl(var(--ink))] leading-relaxed">
                                                    {m.text || ""}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function MiraInbox() {
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);
    const [seeding, setSeeding] = useState(false);

    const load = async (statusFilter) => {
        try {
            const params = statusFilter && statusFilter !== "all" ? { status_filter: statusFilter } : {};
            const { data } = await api.get("/admin/mira/tickets", { params });
            setItems(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        load(filter);
    }, [filter]);

    const seedTest = async () => {
        setSeeding(true);
        try {
            await api.post("/admin/mira/tickets/test", {});
            await load(filter);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSeeding(false);
        }
    };

    const [polling, setPolling] = useState(false);
    const [pollMsg, setPollMsg] = useState("");
    const pullNow = async () => {
        setPolling(true);
        setPollMsg("");
        try {
            const { data } = await api.post("/admin/mira/poll-chatbase", {});
            const c = data.counts || {};
            setPollMsg(`Fetched ${c.fetched || 0} · enriched ${c.enriched || 0} · created ${c.created || 0}`);
            await load(filter);
        } catch (e) {
            setPollMsg(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setPolling(false);
        }
    };

    const [exporting, setExporting] = useState(false);
    const exportCsv = async () => {
        setExporting(true);
        try {
            const params =
                filter && filter !== "all" ? { status_filter: filter } : {};
            const { data } = await api.get("/admin/mira/tickets/export.csv", {
                params,
                responseType: "blob",
            });
            const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
            const today = new Date().toISOString().slice(0, 10);
            const suffix = filter && filter !== "all" ? `-${filter}` : "";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `mira-conversations${suffix}-${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setExporting(false);
        }
    };

    const onTicketChange = (updated) => {
        setItems((prev) => (prev || []).map((t) => (t.id === updated.id ? updated : t)));
        setActive(updated);
    };

    if (err) return <p className="text-[13px] text-[hsl(var(--destructive))]" data-testid="mira-error">{err}</p>;
    if (!items) return <p className="eyebrow" data-testid="mira-loading">Loading…</p>;

    return (
        <div data-testid="mira-inbox">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
                <div className="flex gap-1 border border-[hsl(var(--brass)/0.4)]">
                    {[
                        { k: "all", l: "All" },
                        { k: "new", l: "New" },
                        { k: "in_progress", l: "In progress" },
                        { k: "closed", l: "Closed" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase transition-colors ${
                                filter === f.k
                                    ? "bg-[hsl(var(--brass))] text-[hsl(var(--ivory))]"
                                    : "text-[hsl(var(--muted))] hover:text-[hsl(var(--navy))]"
                            }`}
                            data-testid={`mira-filter-${f.k}`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>
                <button
                    onClick={pullNow}
                    disabled={polling}
                    className="btn-ghost text-[11px] tracking-[0.2em] uppercase disabled:opacity-60"
                    data-testid="mira-pull-now-button"
                    title="Fetch the latest Chatbase conversations now (instead of waiting 5 min)"
                >
                    {polling ? "Pulling…" : "↓ Pull from Chatbase"}
                </button>
                <button
                    onClick={exportCsv}
                    disabled={exporting || !items || items.length === 0}
                    className="btn-ghost text-[11px] tracking-[0.2em] uppercase disabled:opacity-60"
                    data-testid="mira-export-csv-button"
                    title="Download the current ticket list as a CSV (opens cleanly in Excel & Google Sheets)"
                >
                    {exporting ? "Exporting…" : "↓ Export CSV"}
                </button>
                <button
                    onClick={seedTest}
                    disabled={seeding}
                    className="btn-ghost text-[11px] tracking-[0.2em] uppercase disabled:opacity-60"
                    data-testid="mira-seed-test-button"
                    title="Create a fake ticket to preview the inbox"
                >
                    {seeding ? "Seeding…" : "+ Seed test ticket"}
                </button>
            </div>

            {pollMsg && (
                <p className="mb-6 text-[13px] text-[hsl(var(--brass))]" data-testid="mira-pull-result">
                    {pollMsg}
                </p>
            )}

            {items.length === 0 ? (
                <p className="text-[15px] text-[hsl(var(--muted))]" data-testid="mira-empty">
                    No Mira conversations yet. Once Chatbase is wired up to the webhook, every ended chat will land here within seconds.
                </p>
            ) : (
                <div className="overflow-x-auto" data-testid="mira-table">
                    <table className="w-full text-[14px]">
                        <thead>
                            <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] border-b border-[hsl(var(--brass)/0.4)]">
                                <th className="py-3 pr-4">Received</th>
                                <th className="py-3 pr-4">Channel</th>
                                <th className="py-3 pr-4">Visitor</th>
                                <th className="py-3 pr-4">Contact</th>
                                <th className="py-3 pr-4">First message</th>
                                <th className="py-3 pr-4">Status</th>
                                <th className="py-3 pr-4">Email sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => (
                                <tr
                                    key={t.id}
                                    onClick={() => setActive(t)}
                                    className="border-b border-[hsl(var(--brass)/0.2)] cursor-pointer hover:bg-[hsl(var(--cream)/0.5)] transition-colors"
                                    data-testid={`mira-row-${t.id}`}
                                >
                                    <td className="py-3 pr-4 text-[hsl(var(--muted))] whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                                    <td className="py-3 pr-4"><ChannelBadge channel={t.channel || "web"} /></td>
                                    <td className="py-3 pr-4 font-medium">{t.lead_name || "Anonymous"}</td>
                                    <td className="py-3 pr-4 text-[13px]">
                                        {t.lead_email && <div className="text-[hsl(var(--ink))]">{t.lead_email}</div>}
                                        {t.lead_phone && <div className="text-[hsl(var(--muted))]">{t.lead_phone}</div>}
                                        {!t.lead_email && !t.lead_phone && <span className="text-[hsl(var(--muted))]">—</span>}
                                    </td>
                                    <td className="py-3 pr-4 max-w-md truncate italic text-[hsl(var(--ink))]">{t.summary || "—"}</td>
                                    <td className="py-3 pr-4"><StatusPill status={t.status} /></td>
                                    <td className="py-3 pr-4 text-[hsl(var(--muted))]">{t.email_sent ? "Yes" : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {active && (
                <MiraTicketDetail
                    ticket={active}
                    onChange={onTicketChange}
                    onClose={() => setActive(null)}
                />
            )}
        </div>
    );
}

export function WebhookLog() {
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/mira/webhook-log", { params: { limit: 50 } });
            setItems(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (err) return <p className="text-[13px] text-[hsl(var(--destructive))]" data-testid="webhook-log-error">{err}</p>;
    if (!items) return <p className="eyebrow" data-testid="webhook-log-loading">Loading…</p>;

    return (
        <div data-testid="webhook-log">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <p className="text-[13px] text-[hsl(var(--muted))] max-w-xl">
                    Every incoming webhook hit from Chatbase is logged here (even rejected ones). Use this to confirm whether Chatbase is actually reaching us and why.
                </p>
                <button
                    onClick={load}
                    className="btn-ghost text-[11px] tracking-[0.2em] uppercase"
                    data-testid="webhook-log-refresh"
                >
                    ↻ Refresh
                </button>
            </div>

            {items.length === 0 ? (
                <p className="text-[15px] text-[hsl(var(--muted))]" data-testid="webhook-log-empty">
                    No webhook hits recorded yet. If you expect Chatbase to be sending them, have a friend chat with Mira and fill her lead form, then refresh this page.
                </p>
            ) : (
                <div className="space-y-4" data-testid="webhook-log-list">
                    {items.map((log) => (
                        <div
                            key={log.id}
                            className={`border p-5 ${log.signature_ok ? "border-[hsl(var(--brass)/0.4)] bg-[hsl(var(--paper))]" : "border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--cream)/0.4)]"}`}
                            data-testid={`webhook-log-item-${log.id}`}
                        >
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <p className="text-[11px] tracking-[0.22em] uppercase" style={{ color: log.signature_ok ? "hsl(var(--brass))" : "hsl(var(--destructive))" }}>
                                        {log.signature_ok ? "✓ Accepted" : "✗ Rejected"} · {log.signature_reason}
                                    </p>
                                    <p className="text-[14px] mt-1 text-[hsl(var(--ink))]">
                                        <span className="text-[hsl(var(--muted))]">Received</span> {new Date(log.received_at).toLocaleString()}
                                        <span className="text-[hsl(var(--muted))] ml-3">· from</span> {log.source_ip || "—"}
                                        <span className="text-[hsl(var(--muted))] ml-3">· body</span> {log.body_size}B
                                    </p>
                                </div>
                                <button
                                    onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                                    className="text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--muted))] hover:text-[hsl(var(--brass))]"
                                    data-testid={`webhook-log-expand-${log.id}`}
                                >
                                    {expanded === log.id ? "Hide details" : "Show details"}
                                </button>
                            </div>
                            {expanded === log.id && (
                                <div className="mt-4 pt-4 border-t border-[hsl(var(--brass)/0.3)] space-y-3">
                                    <div>
                                        <p className="eyebrow">Headers</p>
                                        <pre className="mt-2 text-[11px] bg-[hsl(var(--cream))] p-3 overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(log.headers, null, 2)}
                                        </pre>
                                    </div>
                                    <div>
                                        <p className="eyebrow">Body (first 4KB)</p>
                                        <pre className="mt-2 text-[11px] bg-[hsl(var(--cream))] p-3 overflow-x-auto whitespace-pre-wrap break-all">
{log.body_preview}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminDashboard() {
    const { admin, loading, logout } = useAuth();
    const { content, loading: cLoading, refresh } = useContent();
    const [tab, setTab] = useState("content");

    if (loading) return null;
    if (!admin) return <Navigate to="/admin/login" replace />;

    return (
        <section className="pt-32 pb-24 min-h-[100svh]" data-testid="admin-dashboard">
            <div className="container-prose">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                        <p className="eyebrow">Private · Admin</p>
                        <h1 className="font-serif text-4xl md:text-5xl mt-3">Editorial desk</h1>
                        <p className="mt-3 text-[14px] text-[hsl(var(--muted))]">Signed in as {admin.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="btn-ghost" data-testid="admin-view-site">View site</Link>
                        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign out</button>
                    </div>
                </div>

                <div className="mt-12 border-b border-[hsl(var(--brass)/0.4)] flex gap-8 flex-wrap">
                    {[
                        { k: "content", l: "Edit content" },
                        { k: "enquiries", l: "Pilot enquiries" },
                        { k: "mira", l: "Inbox — Mira" },
                        // v3.43 / Phase D — Clubs catalogue panel. First
                        // panel of the considered growth pattern; future
                        // panels (Programme Benefits, Tenant Config, Members)
                        // slot in alongside.
                        { k: "clubs", l: "Clubs catalogue" },
                        { k: "venues", l: "Venues catalogue" },
                        // FIR-20260612-006 · Members tab on /admin
                        // (was previously only at /club/admin/members).
                        { k: "members", l: "Members" },
                        // FIR-20260612-012 · /enjoy events overlays panel
                        { k: "enjoy-overlays", l: "Enjoy overlays" },
                        // Founder ruling 18 Feb 2026 PM-12 · /escape admin
                        // surface. Wave A ships the Signature Escapes
                        // editor; other sections (Viator products,
                        // coverage audit, image issues, broken URLs)
                        // are stub-marked "Wave B" but visible in the
                        // tab so the desk can navigate to them later.
                        { k: "escape-catalogue", l: "Escape catalogue" },
                        // Founder ruling 16 Feb 2026 PM-16 · Signature
                        // Experiences image queue (Workstream B). Image
                        // hierarchy + publish gate + Cloudinary upload.
                        { k: "signature-queue", l: "Signature queue" },
                        { k: "admin-urls", l: "Admin URLs" },
                        // v3.46.8 — The Complete Bible: one-button export
                        // of all /app/memory/ founding specs as a single
                        // .docx. Regenerates live on every press; the
                        // markdown is the canon, this is just a snapshot.
                        // Part of the Fort Knox "Documents" vault path.
                        { k: "bible", l: "The Bible" },
                        { k: "webhook-log", l: "Webhook log" },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`pb-4 text-[12px] tracking-[0.22em] uppercase transition-colors ${
                                tab === t.k ? "text-[hsl(var(--brass))] border-b-2 border-[hsl(var(--brass))] -mb-px" : "text-[hsl(var(--muted))] hover:text-[hsl(var(--navy))]"
                            }`}
                            data-testid={`admin-tab-${t.k}`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>

                <div className="mt-12">
                    {tab === "content" && (cLoading ? <p className="eyebrow">Loading…</p> : <Editor initial={content} onSaved={refresh} />)}
                    {tab === "enquiries" && <Enquiries />}
                    {tab === "mira" && <MiraInbox />}
                    {tab === "clubs" && <ClubsPanel />}
                    {tab === "venues" && <VenuesPanel />}
                    {tab === "members" && <MembersPanel />}
                    {tab === "enjoy-overlays" && <EnjoyOverlaysPanel />}
                    {tab === "escape-catalogue" && <EscapeCataloguePanel />}
                    {tab === "signature-queue" && <SignatureQueuePanel />}
                    {tab === "admin-urls" && <AdminUrlsPanel />}
                    {tab === "bible" && <BiblePanel />}
                    {tab === "webhook-log" && <WebhookLog />}
                </div>
            </div>
        </section>
    );
}
